import { ref } from 'vue';

import * as DataDogLogs from '~/lib/datadog/datadog';

const baseUrl = window.Shopify.routes.root;
const headers = {
  'Content-Type': 'application/json',
};
const POST_METHOD = 'POST';

const cart = ref(null);
const fetchInProgress = ref(false);
let fetchPromise = null;

export function useCart() {
  /**
   * Get the cart, returns the cart
   * https://shopify.dev/docs/api/ajax/reference/cart#get-locale-cart-js
   *
   * @param {boolean} force
   * @returns {Promise<Object>}
   */
  const getCart = async (force = false) => {
    if (cart.value && !force) return cart.value;
    if (fetchInProgress.value && !force) {
      return fetchPromise;
    }

    fetchInProgress.value = true;
    fetchPromise = fetch(`${baseUrl}cart.js`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((newCartData) => {
        cart.value = newCartData;
        return newCartData;
      })
      .catch((error) => {
        DataDogLogs.error(
          '[Allbirds composable] - useCart - getCart - Failed to fetch cart',
          { force },
          error,
        );
        cart.value = null;
        throw error;
      })
      .finally(() => {
        fetchInProgress.value = false;
      });
    return fetchPromise;
  };

  /**
   * Add attributes to the cart, returns the updated cart
   * https://shopify.dev/docs/api/ajax/reference/cart#update-cart-attributes
   *
   * @param {Object} attributes
   * @returns {Promise<Object>}
   */
  const addAttributes = (attributes) => {
    return updateCart({
      attributes,
    });
  };

  /**
   * Add a single variant to the cart, returns the updated cart
   *
   * @param {string} variantId
   * @param {number} quantity
   * @param {Object} properties
   * @returns {Promise<Object>}
   */
  const addSingleVariant = (variantId, quantity = 1, properties) => {
    const items = [
      {
        id: variantId,
        properties,
        quantity,
      },
    ];
    return addToCart({ items });
  };

  /**
   * Add multiple items to the cart, returns the updated cart
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
   *
   * @param {Array<Object>} items
   * @returns {Promise<Object>}
   */
  const addToCart = async (items) => {
    fetchInProgress.value = true;
    try {
      await fetch(`${baseUrl}cart/add.js`, {
        body: JSON.stringify(items),
        headers,
        method: POST_METHOD,
      });
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds composable] - useCart - addToCart - Failed to add to cart',
        { items },
        error,
      );
      fetchInProgress.value = false;
      throw error;
    }
    return getCart(true);
  };

  /**
   * Update the cart, returns the updated cart
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-update-js
   *
   * @param {Object} updates
   * @returns {Promise<Object>}
   */
  const updateCart = async (updates) => {
    fetchInProgress.value = true;
    try {
      await fetch(`${baseUrl}cart/update.js`, {
        body: JSON.stringify(updates),
        headers,
        method: POST_METHOD,
      });
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds composable] - useCart - updateCart - Failed to update cart',
        { updates },
        error,
      );
      fetchInProgress.value = false;
      throw error;
    }
    return getCart(true);
  };

  /**
   * Update a single line item, returns the updated cart
   *
   * @param {string} id the variant id or the line item id
   * @param {number} quantity
   * @returns {Promise<Object>}
   */
  const updateSingleLineItem = (id, quantity = 1) => {
    const updates = {
      [id]: quantity,
    };
    return updateCart({ updates });
  };

  /**
   * Remove a single line item, returns the updated cart
   *
   * @param {string} id the variant id or the line item id
   * @returns {Promise<Object>}
   */
  const removeLineItem = (id) => {
    return updateSingleLineItem(id, 0);
  };

  /**
   * Clear the cart, returns the updated cart
   * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-clear-js
   *
   * @returns {Promise<Object>}
   */
  const clearCart = async () => {
    fetchInProgress.value = true;
    try {
      const response = await fetch(`${baseUrl}cart/clear.js`, {
        headers,
        method: POST_METHOD,
      });
      cart.value = await response.json();
    } catch (error) {
      DataDogLogs.error(
        '[Allbirds composable] - useCart - clearCart - Failed to clear cart',
        {},
        error,
      );
      throw error;
    } finally {
      fetchInProgress.value = false;
    }
    return cart.value;
  };

  return {
    addAttributes,
    addSingleVariant,
    addToCart,
    cart,
    clearCart,
    getCart,
    isLoading: fetchInProgress,
    removeLineItem,
    updateCart,
    updateSingleLineItem,
  };
}
